import { ADD_ON_STORE_BATCH_MAX_BYTES, ADD_ON_STORE_BATCH_MAX_ITEMS, HostBridge, hostBridge } from '@publicala/delfino';
import type {
    AddOnCreatePayload,
    AddOnSaveResponse,
    AddOnStoreBatchItem,
    AddOnStoreBatchResponse,
} from '@publicala/delfino';
import type { AddOnDraftRecord, NewAddOnRecord } from '_shared_/UI/AddOns/addOnDraft';
import { isE2EMockActive } from '../e2eMockGuard';
import { isHostValidationError, isMethodNotImplementedError } from '../bridgeErrors';

/**
 * The host's names for the fields the reader writes: the image travels as
 * a URL for the host to fetch or as a file for it to store, never both,
 * and an empty body as an empty string.
 */
function toPayload({
    id: _id,
    imageUrl,
    imageFile,
    body,
    ...fields
}: Partial<AddOnDraftRecord>): Partial<AddOnCreatePayload> {
    return {
        ...fields,
        ...(body !== undefined && { body: body ?? '' }),
        ...(imageFile ? { imageFile } : imageUrl !== undefined && { imageFromUrl: imageUrl }),
    } as Partial<AddOnCreatePayload>;
}

/** A piece of a batch as the host receives it: a file never goes, only an image URL. */
export function storeBatchItem({ imageFile: _imageFile, ...record }: NewAddOnRecord): AddOnStoreBatchItem {
    return toPayload(record) as AddOnStoreBatchItem;
}

const encoder = new TextEncoder();

/**
 * Splits a batch into the requests the host takes: no more pieces than its
 * limit, and no heavier than it weighs a request, the UTF-8 JSON that travels.
 */
export function storeBatchChunks<T>(items: T[], recordOf: (item: T) => NewAddOnRecord): T[][] {
    // The array's brackets, less the comma its first piece never takes.
    const emptyWeight = 1;
    const chunks: T[][] = [];
    let chunk: T[] = [];
    let bytes = emptyWeight;

    for (const item of items) {
        // The piece's JSON and the comma before it.
        const weight = encoder.encode(JSON.stringify(storeBatchItem(recordOf(item)))).length + 1;
        if (
            chunk.length === ADD_ON_STORE_BATCH_MAX_ITEMS ||
            (chunk.length > 0 && bytes + weight > ADD_ON_STORE_BATCH_MAX_BYTES)
        ) {
            chunks.push(chunk);
            chunk = [];
            bytes = emptyWeight;
        }
        chunk.push(item);
        bytes += weight;
    }
    if (chunk.length > 0) chunks.push(chunk);

    return chunks;
}

/**
 * The host would not keep the record as sent. The message is the host's own
 * sentence about the field it refused, fit to show the operator as it is.
 */
export class AddOnRefused extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AddOnRefused';
    }
}

/**
 * Saves add-ons to the publication through the host. Every write answers
 * null when there is no host to ask, so the caller keeps the piece in the
 * reader's own store, as it does in standalone. A record the host refuses
 * comes back as an AddOnRefused, with the host's words.
 */
class AddOnsAPI {
    async store(record: NewAddOnRecord): Promise<AddOnSaveResponse | null> {
        // A create always names a URL, so one that carries a file names a null one.
        return this.ask(() =>
            hostBridge.addOns.store({ imageFromUrl: null, ...toPayload(record) } as AddOnCreatePayload)
        );
    }

    /**
     * @param changes - Only the fields that changed: the host stores the
     *   image and re-derives the page range on every write that carries them.
     */
    async update(id: string | number, changes: Partial<AddOnDraftRecord>): Promise<AddOnSaveResponse | null> {
        return this.ask(() => hostBridge.addOns.update({ addOnId: String(id), data: toPayload(changes) }));
    }

    async delete(id: string | number): Promise<void> {
        await this.ask(() => hostBridge.addOns.delete({ addOnId: String(id) }));
    }

    /**
     * Creates the pieces in one request. The answer is partial: a piece the
     * host refuses comes back among `failed` under its uuid, and the others
     * are stored. An image goes as its URL, which the host fetches once the
     * request has answered, so a stored piece answers without it.
     *
     * A retried uuid answers, among `created`, the piece the first attempt
     * stored, so resending a chunk stores nothing twice. A uuid an earlier
     * request is still storing comes back among `failed`.
     */
    async storeBatch(records: NewAddOnRecord[]): Promise<AddOnStoreBatchResponse | null> {
        if (!this.hasHost()) return null;

        const addOns = records.map(record => storeBatchItem(record));
        const answer = await this.ask(() => hostBridge.addOns.storeBatch({ addOns }));
        if (answer === null) throw new Error('The host cannot store a batch of add-ons.');

        return answer;
    }

    /** Can the pieces of a batch be kept? The host has to take them all at once, or there is no host to ask. */
    canStoreBatch(): boolean {
        if (!this.hasHost()) return true;

        try {
            return hostBridge.remoteImplements('addOns', 'storeBatch') === true;
        } catch {
            return false;
        }
    }

    private hasHost(): boolean {
        return !isE2EMockActive() && !HostBridge.isStandalone();
    }

    private async ask<T>(call: () => Promise<T>): Promise<T | null> {
        if (!this.hasHost()) return null;

        try {
            return await call();
        } catch (error) {
            if (isMethodNotImplementedError(error)) return null;
            if (isHostValidationError(error)) throw new AddOnRefused((error as Error).message);
            throw error;
        }
    }
}

export const addOnsAPI = new AddOnsAPI();
